import { useAuth as useOidcAuth } from 'react-oidc-context';
import { suppressGuard } from '../guards/unsavedChanges';
import { endCognitoSession } from './endSession';
import { sessionProvenanceFromClaims, type SessionProvenance } from './sessionProvenance';
import { clearStoredWorkspaceId } from '../workspace/workspaceStorage';

export function useAuth() {
  const auth = useOidcAuth();
  return {
    user: auth.user
      ? { userId: auth.user.profile.sub, username: auth.user.profile.email ?? auth.user.profile.sub }
      : null,
    isLoading: auth.isLoading,
    signOut: async () => {
      // Security wins over unsaved data: silence the unsaved-changes guard so the
      // sign-out redirect below is never blocked or interrupted by a native
      // beforeunload dialog (covers both manual sign-out and the idle timeout,
      // which routes through here).
      suppressGuard();
      // Revoke the refresh token, drop the local OIDC token store, THEN end the
      // Cognito session at its /logout endpoint. All three matter and none of
      // them substitutes for another: removeUser() alone leaves the Hosted-UI
      // SSO cookie alive (so the next signinRedirect() silently re-logs the
      // user in), and /logout alone leaves a 24h refresh token usable. The
      // sequencing, the failure tolerance, and the reason the /logout host is
      // resolved from OIDC discovery rather than VITE_COGNITO_DOMAIN all live
      // in endSession.ts — read its header before changing any of this.
      //
      // The refresh token MUST be read here, before removeUser() empties the
      // store; endCognitoSession only ever sees the captured value.
      await endCognitoSession({
        refreshToken: auth.user?.refresh_token ?? null,
        clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
        logoutUri: `${window.location.origin}/login`,
        // Also clears the remembered active workspace from this tab's sessionStorage
        // (steward-app#1401) — see `clearStoredWorkspaceId`'s own doc for why a
        // same-tab sign-out/sign-in needs this, why localStorage is deliberately left
        // alone, and why this lives here rather than in `WorkspaceProvider` itself,
        // which isn't mounted by the time this runs.
        clearLocalSession: () => {
          clearStoredWorkspaceId();
          return auth.removeUser();
        },
        navigate: (url) => window.location.assign(url),
      });
    },
  };
}

export function useAccessToken(): string | null {
  const auth = useOidcAuth();
  return auth.user?.access_token ?? null;
}

/**
 * The Cognito ID token for the caller's own signed-in session (Wave 1 social
 * login). Distinct from {@link useAccessToken}: every ordinary API call sends
 * the access token, but appcore's provisioning and link-ceremony endpoints
 * (`POST /me/provision`, `POST /me/logins`) additionally need the ID token in
 * the request body, because the federated provenance (the `identities` claim)
 * only ever appears there, never on the access token. Null before sign-in
 * resolves or if the OIDC library ever omits it (it always requests the
 * `openid` scope, so this should not happen in practice).
 */
export function useIdToken(): string | null {
  const auth = useOidcAuth();
  return auth.user?.id_token ?? null;
}

/**
 * Whether this session signed in through a federated provider (Google) or
 * natively (email/password) — see {@link sessionProvenanceFromClaims} for what
 * decides it and why it is a UI-routing signal only.
 *
 * Read from `user.profile`, which is oidc-client-ts's already-parsed ID token
 * payload, so nothing here re-decodes a JWT. `unknown` while sign-in is still
 * resolving.
 */
export function useSessionProvenance(): SessionProvenance {
  const auth = useOidcAuth();
  return sessionProvenanceFromClaims(auth.user?.profile as Record<string, unknown> | undefined);
}
